/**
 * Баланс §75.
 *
 * Резерв показывается отдельной строкой: без этого непонятно, почему доступных
 * единиц меньше, чем ожидалось, пока идут генерации (§37).
 * История §75 по умолчанию скрывает резерв и его возврат — это внутренняя механика.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LedgerEntryView } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { walletApi } from '../api/wallet';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMoney, formatNumber } from '../i18n/format';
import { errorText } from '../i18n/errors';

const units = (value: number): string => formatNumber(value);

const money = (value: number, currency: string): string =>
  formatMoney(value, currency);

function groupByDay(entries: LedgerEntryView[]): Array<[string, LedgerEntryView[]]> {
  const groups = new Map<string, LedgerEntryView[]>();
  for (const entry of entries) {
    const key = formatDate(entry.createdAt, {
      day: 'numeric',
      month: 'long',
    });
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  }
  return [...groups.entries()];
}

export function BalancePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [topupOpen, setTopupOpen] = useState(false);

  const balance = useQuery({ queryKey: ['wallet'], queryFn: walletApi.balance });
  const ledger = useQuery({ queryKey: ['ledger'], queryFn: () => walletApi.ledger() });
  const packages = useQuery({ queryKey: ['packages'], queryFn: walletApi.packages });

  const topup = useMutation({
    mutationFn: async (packageId: string) => {
      const checkout = await walletApi.checkout(packageId);
      if (checkout.redirectUrl) {
        window.location.href = checkout.redirectUrl;
        return;
      }
      // Демо-оплата: реального шлюза пока нет (вопрос A4).
      await walletApi.mockPay(checkout.paymentId);
    },
    onSuccess: async () => {
      setTopupOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['wallet'] }),
        queryClient.invalidateQueries({ queryKey: ['ledger'] }),
      ]);
    },
  });

  const wallet = balance.data;

  return (
    <div className="flex max-w-[720px] flex-col gap-8">
      <PageHeader title={t('nav.balance')} />

      <div>
        <p className="text-[40px] font-semibold leading-none tracking-tight">
          {units(wallet?.available ?? 0)}
          <span className="ml-2 text-section-title font-normal text-content-secondary">{t('billing.units')}</span>
        </p>
        {wallet && wallet.reserved > 0 ? (
          <p className="mt-3 text-body text-content-secondary">
            {t('billing.reserved', { value: units(wallet.reserved) })}
          </p>
        ) : null}

        <div className="mt-6">
          <Button variant="primary" onClick={() => setTopupOpen((open) => !open)}>{t('billing.topUp')}</Button>
        </div>
      </div>

      {topupOpen ? (
        <Card>
          <CardBody className="flex flex-col gap-4">
            <h2 className="text-card-title">{t('billing.choosePackage')}</h2>

            {topup.isError ? (
              <Alert tone="error">{errorText(topup.error)}</Alert>
            ) : null}

            <div className="flex flex-col gap-2">
              {(packages.data ?? []).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  disabled={topup.isPending}
                  onClick={() => topup.mutate(item.id)}
                  className="flex items-center gap-4 rounded-control border border-line px-4 py-3 text-left transition-colors duration-fast hover:border-line-strong hover:bg-surface-hover disabled:opacity-40"
                >
                  <span className="text-body text-content">
                        {t('billing.unitsValue', { value: units(item.units) })}
                      </span>
                  {item.isBest ? (
                    <span className="rounded border border-line px-1.5 py-0.5 text-[11px] text-content-secondary">{t('billing.better')}</span>
                  ) : null}
                  <span className="ml-auto text-body text-content">
                    {money(item.price, item.currency)}
                  </span>
                </button>
              ))}
            </div>

            <p className="text-meta text-content-muted">{t('billing.demoNotice')}</p>
          </CardBody>
        </Card>
      ) : null}

      <section>
        <h2 className="mb-4 text-section-title font-medium">{t('billing.history')}</h2>

        {(ledger.data?.data ?? []).length === 0 ? (
          <p className="text-body text-content-secondary">{t('billing.noOperations')}</p>
        ) : (
          <div className="flex flex-col gap-6">
            {groupByDay(ledger.data?.data ?? []).map(([day, entries]) => (
              <div key={day}>
                <p className="mb-2 text-meta text-content-muted">{day}</p>
                <div className="flex flex-col">
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between border-b border-line py-2.5 last:border-b-0"
                    >
                      <span className="text-body text-content">{entry.title}</span>
                      <span
                        className={
                          entry.direction === 'in' ? 'text-body text-success' : 'text-body text-content-secondary'
                        }
                      >
                        {entry.direction === 'in' ? '+' : '−'}
                        {units(entry.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
